import { Box, BoxProps } from "@mui/material";
import { ReactElement, useEffect, useRef, useState } from "react";

import { LabelPreview, LabelPreviewProps } from "./LabelPreview";
import {
  LABEL_WIDTH_BY_SIZE,
  PREVIEW_MAX_ZOOM,
  PREVIEW_MIN_ZOOM,
  previewZoomForStage,
} from "./previewZoom";

type LabelPreviewStageProps = Omit<LabelPreviewProps, "fixedWidth"> & {
  /** Print CSS scoping class for the print root -- see
   * ContainerLabelScreen's PRINT_ACTIVE_BODY_CLASS comment. Callers with no
   * print root to scope (e.g. ContainerLabelSection's inline summary) simply
   * omit it, in which case the wrapping Box below gets no className at all. */
  printRootClassName?: string;
  /** Zoom ceiling for the stage's adaptive fit (defaults to
   * PREVIEW_MAX_ZOOM). Summary surfaces embedding the stage next to other
   * page content (e.g. the container detail page's label section) pass a
   * lower ceiling so the artifact stays thumbnail-sized instead of blowing
   * up to fill a wide stage. */
  maxZoom?: number;
  /** Zoom floor for the adaptive fit (defaults to PREVIEW_MIN_ZOOM).
   * Summary surfaces giving the stage less width than the legibility floor
   * assumes pass a lower floor so the label shrinks to fit instead of
   * overflowing into a horizontal scroll. */
  minZoom?: number;
  /** Padding between the dashed stage border and the label (defaults to the
   * full-page stage's roomy `{ xs: 3, md: 4 }`). Thumbnail embeds pass
   * something tighter so the stage's footprint tracks the shrunken label
   * instead of wrapping it in full-size breathing room. */
  stagePadding?: BoxProps["p"];
};

/**
 * The fixed-size "stage" every label preview renders on: a dashed-border
 * staging area around `LabelPreview` with `fixedWidth` set. `fixedWidth` is
 * hardwired on here, not exposed as a prop -- a preview on a stage always
 * renders the fixed-size artifact, so it matches what actually prints/
 * exports, and no caller can opt back into the flexed rendering by mistake.
 */
export function LabelPreviewStage({
  printRootClassName,
  maxZoom = PREVIEW_MAX_ZOOM,
  minZoom = PREVIEW_MIN_ZOOM,
  stagePadding = { xs: 3, md: 4 },
  ...previewProps
}: LabelPreviewStageProps): ReactElement {
  const stageRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(maxZoom);
  // LabelPreview defaults `size` to "lg" when omitted -- mirrored here so the
  // zoom fits the same label width LabelPreview actually renders.
  const labelWidth = LABEL_WIDTH_BY_SIZE[previewProps.size ?? "lg"];

  useEffect(() => {
    const stage = stageRef.current;
    // jsdom (unit tests) has no ResizeObserver polyfill -- guarding it out
    // keeps those tests on the initial `maxZoom` instead of throwing.
    if (!stage || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      setZoom(previewZoomForStage(stage.clientWidth, labelWidth, maxZoom, minZoom));
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, [labelWidth, maxZoom, minZoom]);

  return (
    // The "stage" backdrop: a dashed-border staging area so the preview
    // reads as a rendered artifact, not a page component. The label itself
    // is fixed-size (see LabelPreview's `fixedWidth`) and never reflows --
    // this box scrolls horizontally instead of shrinking it when the
    // viewport is narrower than the label.
    <Box
      ref={stageRef}
      data-testid="label-preview-stage"
      sx={{
        p: stagePadding,
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "surfaceSubtle",
        overflowX: "auto",
        display: "flex",
      }}
    >
      {/* mx: "auto" (not the parent's justifyContent: "center") -- centering
          a wider-than-container child with the PARENT's justifyContent
          clips its left overflow outside the scrollable area, so you can
          scroll right but never reach the left edge. Auto margins center it
          when there's room and collapse to 0 on overflow, keeping both
          edges reachable. */}
      <Box
        {...(printRootClassName ? { className: printRootClassName } : {})}
        data-testid="label-preview-zoom"
        sx={{ zoom, mx: "auto" }}
      >
        <LabelPreview {...previewProps} fixedWidth />
      </Box>
    </Box>
  );
}
